function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function displayText(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return undefined
}

function firstText(record: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const text = displayText(record[key])
    if (text) return text
  }
  return undefined
}

function listItemText(value: unknown): string | undefined {
  const scalar = displayText(value)
  if (scalar) return scalar
  if (!isRecord(value)) return undefined
  return firstText(value, ['text', 'label', 'word', 'title', 'name', 'id'])
}

function listText(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined
  const items = value.map(listItemText).filter((item): item is string => Boolean(item))
  return items.length > 0 ? items.join(' / ') : undefined
}

function factListText(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined
  const facts = value.map((entry) => {
    if (!isRecord(entry)) return displayText(entry)
    const name = firstText(entry, ['name', 'title', 'label'])
    const detail = firstText(entry, ['text', 'quote', 'question', 'statement'])
    if (name && detail) return `${name}：${detail}`
    return detail ?? name
  }).filter((item): item is string => Boolean(item))
  return facts.length > 0 ? facts.join('；') : undefined
}

function subQuestionListText(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined
  const questions = value.map((entry, index) => {
    if (!isRecord(entry)) return undefined
    const question = firstText(entry, ['question', 'prompt', 'instruction'])
    if (!question) return undefined
    const options = listText(entry.options)
    return `${index + 1}. ${question}${options ? `（${options}）` : ''}`
  }).filter((item): item is string => Boolean(item))
  return questions.length > 0 ? questions.join('；') : undefined
}

/** Converts a frozen learner-facing question object into durable text evidence. */
export function createFormativeQuestionEvidenceSnapshot(snapshot: unknown): string | undefined {
  const scalar = displayText(snapshot)
  if (scalar) return scalar
  if (!isRecord(snapshot)) return undefined

  const lines: string[] = []
  const seen = new Set<string>()
  const addLine = (label: string, value: string | undefined) => {
    if (!value || seen.has(value)) return
    seen.add(value)
    lines.push(`${label}：${value}`)
  }

  const primary = firstText(snapshot, ['question', 'prompt', 'instruction', 'target', 'word', 'title'])
  addLine('题目', primary)
  addLine('要求', firstText(snapshot, ['instruction', 'prompt']))
  addLine('线索', firstText(snapshot, ['clue', 'passage', 'story', 'fact', 'context']))
  addLine('听力材料', firstText(snapshot, ['speech', 'audioText', 'transcript']))
  addLine('朗读内容', firstText(snapshot, ['target', 'sentence']))
  addLine('中文提示', firstText(snapshot, ['cn', 'meaning']))
  addLine('情境', firstText(snapshot, ['scene']))
  addLine('图片资源', firstText(snapshot, ['imageUrl']))
  addLine('选项', listText(snapshot.options) ?? listText(snapshot.replies))
  addLine('可用词', listText(snapshot.tokens) ?? listText(snapshot.words) ?? listText(snapshot.tiles))
  addLine('已知信息', factListText(snapshot.facts) ?? factListText(snapshot.suspectStatements))
  addLine('子题', subQuestionListText(snapshot.logicQuestions) ?? subQuestionListText(snapshot.subQuestions))

  return lines.length > 0 ? lines.join('\n') : undefined
}

export interface FormativeQuestionEvidenceSection {
  label: string
  text: string
  items?: string[]
}

export interface FormativeQuestionEvidenceParts {
  question: string
  sections: FormativeQuestionEvidenceSection[]
  choices: string[]
  choicesLabel: string
  imageUrl?: string
}

const EVIDENCE_LABELS = new Set([
  '题目',
  '要求',
  '线索',
  '听力材料',
  '朗读内容',
  '中文提示',
  '情境',
  '图片资源',
  '选项',
  '可用词',
  '已知信息',
  '子题',
])

export function parseFormativeQuestionEvidenceSnapshot(
  snapshot: string | undefined,
): FormativeQuestionEvidenceParts | null {
  const source = snapshot?.trim()
  if (!source) return null

  let question = ''
  let choices: string[] = []
  let choicesLabel = '选项'
  let imageUrl: string | undefined
  const sections: FormativeQuestionEvidenceSection[] = []

  for (const line of source.split(/\r?\n/).map((value) => value.trim()).filter(Boolean)) {
    const separator = line.indexOf('：')
    const label = separator > 0 ? line.slice(0, separator) : ''
    const text = separator > 0 ? line.slice(separator + 1).trim() : line
    if (!EVIDENCE_LABELS.has(label)) {
      question ||= line
      continue
    }
    if (label === '题目') {
      question = text
      continue
    }
    if (label === '选项' || label === '可用词') {
      choices = text.split(' / ').map((value) => value.trim()).filter(Boolean)
      choicesLabel = label
      continue
    }
    if (label === '图片资源') {
      imageUrl = text
      continue
    }
    const items = label === '已知信息' || label === '子题'
      ? text.split('；').map((value) => value.trim()).filter(Boolean)
      : undefined
    sections.push({ label, text, ...(items && items.length > 1 ? { items } : {}) })
  }

  const knownInformation = sections.find((section) => section.label === '已知信息' && section.items)
  const normalizedSections = sections.flatMap((section) => {
    if (section.label !== '线索' || !knownInformation?.items) return [section]
    const condition = knownInformation.items
      .reduce((text, item) => text.replace(item, ''), section.text)
      .replace(/\s+/g, ' ')
      .trim()
    if (!condition) return []
    if (condition === section.text) return [section]
    return [{ label: '条件', text: condition }]
  })

  return {
    question: question || source,
    sections: normalizedSections,
    choices,
    choicesLabel,
    ...(imageUrl ? { imageUrl } : {}),
  }
}
